import React, { createElement } from 'react'
import { Spin } from 'antd'
import pathToRegexp from 'path-to-regexp'
import Loadable from 'react-loadable'
import { getMenuData } from './menu'
import { MenuItem, DynamicWrapperReturnType } from './typings.d'

let routerDataCache: RouterData

interface FlatMenu {
  [index: string]: MenuItem
}

/**
 * 扁平化菜单，用路径作为 key
 *
 * [{                                 {
 *   authority: 'admin',               '/aaa': {
 *   children: [{                        authority: 'admin',
 *     authority: 'admin',               children: [{
 *     name: 'BBB',                        authority: 'admin',
 *     path: '/aaa/bbb'                    name: 'BBB',
 *   }],                     ==>           path: '/aaa/bbb'
 *   icon: 'AAA',                        }],
 *   name: 'AAA',                        icon: 'AAA',
 *   path: '/AAA'                        name: 'AAA',
 * }]                                    path: '/aaa',
 *                                     },
 *                                    '/aaa/bbb': {
 *                                      authority: 'admin',
 *                                      name: 'BBB',
 *                                      path: '/aaa/bbb'
 *                                    }
 *                                  }
 *
 * @param {MenuItem[]} menus 格式化后的菜单数据
 * @returns {FlatMenu} 扁平化后的菜单数据
 */
function getFlatMenuData(menus: MenuItem[]): FlatMenu {
  let keys = {}

  menus.forEach(item => {
    if (item.children) {
      keys[item.path] = { ...item }
      keys = { ...keys, ...getFlatMenuData(item.children) }
    } else {
      keys[item.path] = { ...item }
    }
  })

  return keys
}

/**
 * 找出在 FlatMenu 数据中对应的 key 值（即路径）
 *
 * @param {FlatMenu} menuData 扁平化后的菜单数据
 * @param {string} path 访问路径
 * @returns {string} FlatMenu 中的 key
 */
function findMenuKey(menuData: FlatMenu, path: string): string {
  const menuKey = Object.keys(menuData).find(key => pathToRegexp(path).test(key))

  if (menuKey) return menuKey

  if (path === '/') {
    return ''
  }

  const lastIdx = path.lastIndexOf('/')
  if (lastIdx < 0) {
    return ''
  }

  if (lastIdx === 0) {
    return findMenuKey(menuData, '/')
  }

  // 如果没有，使用上一层的配置
  return findMenuKey(menuData, path.substr(0, lastIdx))
}

export interface RouterData {
  [index: string]: {
    component: DynamicWrapperReturnType
    name: string
    authority: string | string[]
    hideInBreadcrumb: boolean
    inherited: boolean
  }
}

// TODO: refactor with React.lazy
/* eslint-disable @typescript-eslint/no-object-literal-type-assertion,@typescript-eslint/no-use-before-define */
export const getRouterData = (): RouterData => {
  const routerConfig = {
    /** 首页 */
    '/': {
      component: dynamicWrapper(() => import('-/layouts/basic/BasicLayout')),
    },
    /** 药品管理 - DrugSKU */
    '/drug-management/drug/sku/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSkuList')),
    },
    '/drug-management/drug/sku/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSkuDetail')),
    },
    '/drug-management/drug/apply/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugApplyDetail')),
    },
    '/drug-management/drug/sku/edit/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSkuDetail')),
    },
    '/drug-management/drug/spu/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSpuList')),
    },
    '/drug-management/drug/spu/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSpuDetail')),
    },
    '/drug-management/drug/spu/apply': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSpuApply')),
    },
    '/drug-management/drug/spu/packAdd': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSpuApplyAddPack')),
    },
    '/drug-management/drug/spu/sale': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/drugSku/DrugSpuApplyForSale')),
    },
    /** 药品管理 - MachineSku */
    '/drug-management/machine/sku/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/machineSku/MachineSkuList')),
    },
    '/drug-management/machine/sku/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/machineSku/MachineSkuDetail')),
    },
    '/drug-management/machine/spu/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/machineSku/MachineSpuList')),
    },
    '/drug-management/machine/spu/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/machineSku/MachineSpuDetail')),
    },
    /** 药品管理 - HealthSKU */
    '/drug-management/health/sku/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthSkuList')),
    },
    '/drug-management/health/sku/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthSkuDetail')),
    },
    '/drug-management/health/spu/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthSpuList')),
    },
    '/drug-management/health/spu/apply': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthSpuApply')),
    },
    '/drug-management/health/apply/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthApplyDetail')),
    },
    '/drug-management/health/spu/packApply': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthSpuApplyForPack')),
    },
    '/drug-management/health/spu/sale': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthSpuApplyForSale')),
    },
    '/drug-management/health/spu/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/healthSku/HealthSpuDetail')),
    },
    /** 药品管理 - CosmeticSKU */
    '/drug-management/cosmetic/sku/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/cosmeticSku/CosmeticSkuList')),
    },
    '/drug-management/cosmetic/sku/detail/:id': {
      component: dynamicWrapper(() =>
        import('-/pages/drugManagement/cosmeticSku/CosmeticSkuDetail')
      ),
    },
    '/drug-management/cosmetic/spu/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/cosmeticSku/CosmeticSpuList')),
    },
    '/drug-management/cosmetic/spu/detail/:id': {
      component: dynamicWrapper(() =>
        import('-/pages/drugManagement/cosmeticSku/CosmeticSpuDetail')
      ),
    },
    /** 药品管理 - ChineseSKU  */
    '/drug-management/chinese/sku/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseSkuList')),
    },
    '/drug-management/chinese/sku/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseSkuDetail')),
    },
    '/drug-management/chinese/spu/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseSpuList')),
    },
    '/drug-management/chinese/spu/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseSpuDetail')),
    },
    '/drug-management/chinese/spu/apply': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseSpuApply')),
    },
    '/drug-management/chinese/apply/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseApplyDetail')),
    },
    '/drug-management/chinese/spu/packApply': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseSpuApplyForPack')),
    },
    '/drug-management/chinese/spu/sale': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/chineseSku/ChineseSpuApplyForSale')),
    },
    '/prescription-management/online/list': {
      component: dynamicWrapper(() => import('-/pages/prescriptionManagement/PrescriptionList')),
    },
    '/prescription-management/online/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/prescriptionManagement/PrescriptionDetail')),
    },
    /** 药品管理 -生活用品 - LifeSKU */
    '/drug-management/life/sku/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/lifeSku/LifeSkuList')),
    },
    '/drug-management/life/sku/detail/:id': {
      component: dynamicWrapper(() =>
        import('-/pages/drugManagement/lifeSku/LifeSkuDetail')
      ),
    },
    '/drug-management/life/spu/list': {
      component: dynamicWrapper(() => import('-/pages/drugManagement/lifeSku/LifeSpuList')),
    },
    '/drug-management/life/spu/detail/:id': {
      component: dynamicWrapper(() =>
        import('-/pages/drugManagement/lifeSku/LifeSpuDetail')
      ),
    },
    /** 补方 */
    '/prescription-management/sup-pres/list': {
      component: dynamicWrapper(() =>
        import('-/pages/supplementPrescription/SupplementPrescriptionList')
      ),
    },
    '/prescription-management/sup-pres/detail/:id': {
      component: dynamicWrapper(() =>
        import('-/pages/supplementPrescription/SupplementPrescriptionDetail')
      ),
    },
    '/prescription-management/sup-pres/add': {
      component: dynamicWrapper(() =>
        import('-/pages/supplementPrescription/SupplementPrescriptionAdd')
      ),
    },
    // '/prescription-management/offline/list': {
    //   component: dynamicWrapper(() =>
    //     import('-/pages/prescriptionManagement/OfflinePrescriptionList')
    //   ),
    // },
    // '/prescription-management/offline/detail/:id': {
    //   component: dynamicWrapper(() =>
    //     import('-/pages/prescriptionManagement/OfflinePrescriptionDetail')
    //   ),
    // },
    '/settlement/list': {
      component: dynamicWrapper(() => import('-/pages/settlement/SettlementList')),
    },
    '/order-management/order/list': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/OrderList')),
    },
    '/order-management/order/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/OrderDetail')),
    },
    '/order-management/order/create-refund/:id': {
      component: dynamicWrapper(() => import('-/pages/afterSaleManagement/RefundSlipDetail')),
    },
    '/order-management/invoice/list': {
      component: dynamicWrapper(() => import('-/pages/invoiceManagement/InvoiceList')),
    },
    '/order-management/orderPush/list': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/OrderPushList')),
    },
    '/order-management/orderPush/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/OrderDetail')),
    },
    '/order-management/supervise': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/SuperviseEntry')),
    },
    '/order-management/superviseEntry/detail/:id/:skuId': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/SuperviseEntryId'))
    },
    '/order-management/superviseDetail/list': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/SuperviseDetail')),
    },
    '/order-management/superviseDetail/detail/:id/:skuId': {
      component: dynamicWrapper(() => import('-/pages/orderManagement/SuperviseDetailId')),
    },
    // 对码管理
    '/code-management/storeCode': {
      component: dynamicWrapper(() => import('-/pages/codeManagement/storeCodeManagement/StoreCodeList')),
    },
    '/code-management/goodsCode': {
      component: dynamicWrapper(() => import('-/pages/codeManagement/goodsCodeManagement/GoodsCodeList')),
    },
    '/freight-management/list': {
      component: dynamicWrapper(() => import('-/pages/freightManagement/TemplateList')),
    },
    '/freight-management/add': {
      component: dynamicWrapper(() => import('-/pages/freightManagement/AddTemplate')),
    },
    '/freight-management/edit/:id': {
      component: dynamicWrapper(() => import('-/pages/freightManagement/EditTemplate')),
    },
    '/freight-management/select-drugs': {
      component: dynamicWrapper(() => import('-/pages/freightManagement/SelectDrugs')),
    },
    '/freight-management/relation/:id': {
      component: dynamicWrapper(() => import('-/pages/freightManagement/RelationList')),
    },
    '/user-management/list': {
      component: dynamicWrapper(() => import('-/pages/userManagement/UserManageList')),
    },
    '/user-management/add': {
      component: dynamicWrapper(() => import('-/pages/userManagement/UserManageAdd')),
    },
    '/user-management/edit/:userId': {
      component: dynamicWrapper(() => import('-/pages/userManagement/UserManageEdit')),
    },
    '/after-sale-management/refund-slip/list': {
      component: dynamicWrapper(() => import('-/pages/afterSaleManagement/RefundSlipList')),
    },
    '/after-sale-management/after-order/list': {
      component: dynamicWrapper(() => import('-/pages/afterSaleManagement/AfterSalesOrderList')),
    },
    '/order-management/order/create-after-sales/:id': {
      component: dynamicWrapper(() => import('-/pages/afterSaleManagement/AfterSalesOrderDetail')),
    },
    '/after-sale-management/after-order/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/afterSaleManagement/AfterSalesOrderDetail')),
    },
    // '/after-sale-management/refund-slip/detail/:id': {
    //   component: dynamicWrapper(() => import('-/pages/afterSaleManagement/RefundSlipDetail')),
    // },
    '/stock-warning': {
      component: dynamicWrapper(() => import('-/pages/stockWarning/StockWarning')),
    },
    /** ------------------- 消息通知 -------------------- */
    '/notification': {
      component: dynamicWrapper(() => import('-/pages/notification/NotificationList')),
    },
    /** ------------------- 设置 -------------------- */
    '/setting': {
      component: dynamicWrapper(() => import('-/pages/settingPage/SettingPage')),
    },
    '/model-setting': {
      component: dynamicWrapper(() => import('-/pages/modelSettingPage/ModelSettingPage')),
    },
    '/config-setting': {
      component: dynamicWrapper(() => import('-/pages/configPage/ConfigPage')),
    },
    /** ------------------- 大客户管理 -------------------- */
    '/key-account/discount': {
      component: dynamicWrapper(() => import('-/pages/keyAccountManagement/KeyAccountManagement')),
    },
    '/key-account/account/:id': {
      component: dynamicWrapper(() => import('-/pages/keyAccountManagement/KeyAccountList')),
    },
    /** ------------------- 合作订单管理 --------------------- */
    '/partner-order/outbound': {
      component: dynamicWrapper(() => import('-/pages/partnerManagement/PartnerOutboundList')),
    },
    '/partner-order/warehousing/list': {
      component: dynamicWrapper(() => import('-/pages/partnerManagement/PartnerWarehousingList')),
    },
    '/partner-order/warehousing/detail/:id': {
      component: dynamicWrapper(() => import('-/pages/partnerManagement/PartnerWarehousingDetail')),
    },
    /** ------------------- 个人中心 -------------------- */
    '/personal': {
      component: dynamicWrapper(() => import('-/pages/personalPage/PersonalPage')),
    },
    /** ------------------开店--------------- */
    '/open-store/process-query': {
      component: dynamicWrapper(() => import('-/pages/openStore/ProcessQuery')),
    },
    '/open-store/basic-info': {
      component: dynamicWrapper(() => import('-/pages/openStore/BasicInfo')),
    },
    '/open-store/business-info': {
      component: dynamicWrapper(() => import('-/pages/openStore/BusinessInfo')),
    },
    '/open-store/agreement-sign': {
      component: dynamicWrapper(() => import('-/pages/openStore/AgreementSign')),
    },
    // NOTE: 新增路由不要放在 /user 的下方
    '/user': {
      component: dynamicWrapper(() => import('-/layouts/user/UserLayout')),
    },
    '/user/login': {
      component: dynamicWrapper(() => import('-/pages/login/Login')),
    },
    '/403': {
      component: dynamicWrapper(() => import('-/pages/403')),
    },
  }
  // Get name from ./menu.js or just set it in the router data.
  const menuData = getFlatMenuData(getMenuData())

  // Route configuration data
  // eg. {name,authority ...routerConfig }
  const routerData = {}
  // The route matches the menu
  Object.keys(routerConfig).forEach(path => {
    // Regular match item name
    // eg.  router /user/:id === /user/chen
    let menuKey = Object.keys(menuData).find(key => pathToRegexp(path).test(`${key}`))
    const inherited = menuKey === undefined

    if (inherited) {
      menuKey = findMenuKey(menuData, path)
    }
    let menuItem: MenuItem = {
      name: '',
      authority: undefined,
      hideInBreadcrumb: undefined,
      path: '',
    }
    // If menuKey is not empty
    if (menuKey) {
      menuItem = menuData[menuKey]
    }
    let router = routerConfig[path]
    // If you need to configure complex parameter routing,
    // https://v1.pro.ant.design/docs/router-and-nav-cn#%E5%B8%A6%E5%8F%82%E6%95%B0%E7%9A%84%E8%B7%AF%E7%94%B1/%E8%8F%9C%E5%8D%95
    // eg . /list/:type/user/info/:id
    router = {
      ...router,
      name: router.name || menuItem.name,
      authority: router.authority || menuItem.authority,
      hideInBreadcrumb: router.hideInBreadcrumb || menuItem.hideInBreadcrumb,
      inherited,
    }
    routerData[path] = router
  })

  return routerData
}

const getRouterDataCache = (): RouterData => {
  if (!routerDataCache) {
    routerDataCache = getRouterData()
  }
  return routerDataCache
}

interface ComponentProps {
  routerData: RouterData
}
// wrapper of dynamic
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dynamicWrapper(component: () => Promise<{ default?: any }>): DynamicWrapperReturnType {
  return Loadable({
    loader: () =>
      component().then(raw => {
        const Component = raw.default || raw

        return <P extends {}>(props: P): React.ReactElement<P & ComponentProps> =>
        // @ts-ignore
          createElement(Component, {
            ...(props as {}),
            routerData: getRouterDataCache(),
          } as P & ComponentProps)
      }),
    loading: () => <Spin size="large" className="global-spin" />, // eslint-disable-line
  })
}

export default {
  getRouterData,
}
